import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { components, OptionProps, SingleValueProps } from "react-select";
import ReactCountryFlag from "react-country-flag";
import classNames from "classnames";
import { GetCountries } from "../../../actions/catalog";
import { DROPDOWN_STYLES } from "../../../constants/theme";

// types
import { PhoneCountryInputProps } from "../../../types/components/dynamicForms/dynamicField";
import { PhoneCountryRecord } from "../../../types/components/dynamicForms";
import { RootState } from "../../../types/redux";

/**
 * An option whose value is the FULL country record (matching every other phone
 * field in the app and §4). We compare/select by `.id`.
 */
interface CountryOpt {
  label: string;
  value: PhoneCountryRecord;
}

/** Menu is wider than the fixed-width code control so full country names fit. */
const MENU_WIDTH = 300;

/** Alpha-2 code for react-country-flag; `iso` is alpha-3, so only a 2-char `isoCode` qualifies. */
const flagCode = (country: PhoneCountryRecord): string | undefined =>
  country.isoCode && country.isoCode.length === 2 ? country.isoCode : undefined;

const renderFlag = (country: PhoneCountryRecord): React.ReactNode => {
  const code = flagCode(country);
  if (!code) return null;
  return (
    <ReactCountryFlag
      countryCode={code}
      svg
      style={{ width: "1.25em", height: "1.25em", marginRight: "0.5em" }}
      aria-hidden
    />
  );
};

/** Selected-value renderer: flag + "+<phoneCode>". */
const SingleValue = (props: SingleValueProps<CountryOpt, false>) => (
  <components.SingleValue {...props}>
    {renderFlag(props.data.value)}+{props.data.value.phoneCode}
  </components.SingleValue>
);

/** Option renderer: flag + "+<phoneCode> <name>". Colour comes from DROPDOWN_STYLES, same as SelectInput. */
const Option = (props: OptionProps<CountryOpt, false>) => {
  const className = classNames("phone-country-code-select-option-label", {
    selected: props.isSelected,
  });
  return (
    <components.Option {...props}>
      {renderFlag(props.data.value)}
      <span className={className}>+{props.data.value.phoneCode}</span>{" "}
      {props.data.label}
    </components.Option>
  );
};

/**
 * Compound phone input: country-code selector + number field, value
 * `{ number, country, type? }`. No react-hook-form awareness.
 *
 * Owns its country catalog (state.catalog.countries) like the legacy
 * PhoneNumberField, so no parent has to remember to fetch. GetCountries is
 * cached (localStorage, 10 min) and only dispatched while the catalog is empty.
 *
 * The country option value is the full catalog record; on change we store it
 * directly as `country`, spreading the existing value so `number` and any stray
 * server field (e.g. `type` from the 226 echo) survive untouched — lossless.
 * Selection is matched by `country.id`.
 */
const PhoneInput = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  describedById,
  invalid,
  placeholder,
  fixedCountry,
}: PhoneCountryInputProps) => {
  const dispatch = useDispatch();
  const countries = useSelector((state: RootState) => state.catalog.countries) as
    | PhoneCountryRecord[]
    | undefined;
  const hasCountries = Boolean(countries?.length);

  useEffect(() => {
    if (!hasCountries) dispatch(GetCountries());
  }, [hasCountries, dispatch]);

  const options: CountryOpt[] = (countries ?? []).map((c) => ({
    label: c.name,
    value: c,
  }));

  const currentId = value?.country?.id;
  const selected =
    currentId != null
      ? options.find((o) => o.value.id === currentId) ?? null
      : null;

  return (
    <div className="bwp-field__phone phone-number-field" data-testid="phone-number-field">
      <div className="bwp-field__phone-code">
        <Select<CountryOpt, false>
          inputId={`${id}-country`}
          className="bwp-field__control bwp-field__control--select"
          classNamePrefix="bwp-select"
          components={{ SingleValue, Option, IndicatorSeparator: null }}
          isDisabled={disabled || fixedCountry}
          placeholder="+"
          isLoading={!options.length}
          options={options}
          value={selected}
          onChange={(opt) =>
            onChange({ ...value, country: opt ? opt.value : value?.country })
          }
          onBlur={onBlur}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          styles={{
            ...DROPDOWN_STYLES,
            menu: (base) => ({ ...base, width: MENU_WIDTH }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
          aria-label="Country code"
        />
      </div>
      <div className="bwp-field__phone-number">
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          className={classNames("form-control", "bwp-field__control", {
            "bwp-field__control--invalid": invalid,
          })}
          value={value?.number ?? ""}
          onChange={(e) => onChange({ ...value, number: e.target.value })}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          aria-describedby={describedById}
        />
      </div>
    </div>
  );
};

export default PhoneInput;